import { useRef } from 'react'
import { flushSync } from 'react-dom'
import { Monitor, Moon, Sun } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { cn } from '@/lib/utils'

const themeOptions = [
  { value: 'light', label: 'Light theme', Icon: Sun },
  { value: 'dark', label: 'Dark theme', Icon: Moon },
  { value: 'system', label: 'System theme', Icon: Monitor },
] as const

interface ThemeViewTransition {
  ready: Promise<void>
  finished: Promise<void>
}

type ThemeTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => ThemeViewTransition
}

interface ThemeToggleProps {
  className?: string
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const currentIndex = themeOptions.findIndex(({ value }) => value === theme)
  const current = themeOptions[currentIndex] ?? themeOptions[0]
  const next = themeOptions[(currentIndex + 1) % themeOptions.length] ?? themeOptions[0]
  const CurrentIcon = current.Icon

  function applyTheme() {
    const root = document.documentElement
    const resolvedTheme =
      next.value === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : next.value

    root.classList.remove('light', 'dark')
    root.classList.add(resolvedTheme)
    flushSync(() => setTheme(next.value))
  }

  function cycleTheme() {
    const origin = buttonRef.current
    const transitionDocument = document as ThemeTransitionDocument

    if (
      !origin ||
      !transitionDocument.startViewTransition ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      applyTheme()
      return
    }

    const { top, left, width, height } = origin.getBoundingClientRect()
    const x = left + width / 2
    const y = top + height / 2
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight
    const radius = Math.hypot(Math.max(x, viewportWidth - x), Math.max(y, viewportHeight - y))
    const clipPath: [string, string] = [
      `circle(0px at ${x}px ${y}px)`,
      `circle(${radius}px at ${x}px ${y}px)`,
    ]
    const root = document.documentElement

    root.dataset.themeTransition = 'active'
    root.style.setProperty('--theme-transition-origin', clipPath[0])

    const transition = transitionDocument.startViewTransition(applyTheme)
    transition.finished.finally(() => {
      delete root.dataset.themeTransition
      root.style.removeProperty('--theme-transition-origin')
    })
    transition.ready.then(() => {
      root.animate(
        { clipPath },
        {
          duration: 400,
          easing: 'ease-in-out',
          fill: 'forwards',
          pseudoElement: '::view-transition-new(root)',
        }
      )
    })
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={cycleTheme}
      title={`${current.label} — switch to ${next.label.toLowerCase()}`}
      aria-label={`${current.label}. Switch to ${next.label.toLowerCase()}`}
      className={cn(
        'inline-flex size-9 cursor-pointer items-center justify-center rounded-full border border-border/70 bg-background/70 text-muted-foreground shadow-xs transition-colors hover:border-brand-orange/50 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      <CurrentIcon className="size-4" aria-hidden="true" />
    </button>
  )
}
